import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, CalendarClock, KanbanSquare, Mail, PhoneCall, Sparkles, Users } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { crmApi, type ApiRecord } from '../../lib/api';
import { SurfaceCard } from '../../components/ui/SurfaceCard';

function formatRelativeDate(value: unknown): string {
	if (!value || typeof value !== 'string') return '';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '';
	const now = new Date();
	const diffMs = d.getTime() - now.getTime();
	const days = Math.round(diffMs / 86_400_000);
	if (days === 0) return 'today';
	if (days === 1) return 'tomorrow';
	if (days === -1) return 'yesterday';
	if (days < 0) return `${Math.abs(days)}d overdue`;
	if (days < 7) return `in ${days}d`;
	return d.toLocaleDateString();
}

function fullName(c: ApiRecord): string {
	const first = String(c.first_name ?? '').trim();
	const last = String(c.last_name ?? '').trim();
	const full = `${first} ${last}`.trim();
	return full || String(c.email ?? `#${c.id}`);
}

export function DashboardPage() {
	const { user } = useAuth();
	const userId = user?.id != null ? String(user.id) : undefined;

	const followUpsQuery = useQuery({
		queryKey: ['my-day', 'follow-ups', userId],
		queryFn: () => crmApi.contactFollowUps(userId ? { assigned_to: userId, limit: 10 } : { limit: 10 })
	});
	const opportunitiesQuery = useQuery({
		queryKey: ['my-day', 'opportunities', userId],
		queryFn: () => crmApi.opportunities(userId ? { owner_id: userId, limit: 10 } : { limit: 10 })
	});
	const leadsQuery = useQuery({
		queryKey: ['my-day', 'new-leads'],
		queryFn: () => crmApi.contacts({ lead_status: 'new', limit: 8 })
	});
	const businessUnitsQuery = useQuery({ queryKey: ['business-units'], queryFn: crmApi.businessUnits });

	const followUps = followUpsQuery.data ?? [];
	const opportunities = opportunitiesQuery.data ?? [];
	const newLeads = leadsQuery.data ?? [];
	const businessUnits = businessUnitsQuery.data ?? [];

	const openOpps = opportunities.filter((o) => !['Won', 'Lost'].includes(String(o.stage ?? '')));
	const overdueCount = followUps.filter((c) => {
		const d = c.follow_up_date ? new Date(String(c.follow_up_date)) : null;
		return d && d.getTime() < Date.now();
	}).length;

	return (
		<div className="space-y-4">
			<section className="rounded-xl border border-subtle bg-surface px-5 py-5 shadow-panel">
				<p className="text-xs font-semibold uppercase tracking-wider text-accent">Today</p>
				<h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
					{user?.name ? `Good ${getGreeting()}, ${user.name.split(' ')[0]}` : 'Welcome back'}
				</h1>
				<p className="mt-0.5 text-sm text-muted">{getSubtitle(followUps.length, overdueCount, openOpps.length, newLeads.length)}</p>
				<div className="mt-4 grid gap-2 sm:grid-cols-4">
					<MiniStat label="Follow-ups due" value={String(followUps.length)} accent={overdueCount > 0 ? 'urgent' : 'normal'} sub={overdueCount > 0 ? `${overdueCount} overdue` : 'on track'} />
					<MiniStat label="My open deals" value={String(openOpps.length)} sub={`${businessUnits.length} business unit${businessUnits.length === 1 ? '' : 's'}`} />
					<MiniStat label="New leads in queue" value={String(newLeads.length)} sub="ready to research" />
					<MiniStat label="Business units" value={String(businessUnits.length)} sub="active pipelines" />
				</div>
			</section>

			<section className="grid gap-3 xl:grid-cols-[1.1fr_0.9fr]">
				<SurfaceCard
					title="Follow-ups due"
					action={<Link to="/contacts" className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-xs text-secondary hover:bg-surface-hover">All contacts <ArrowRight className="h-3.5 w-3.5" /></Link>}
				>
					{followUps.length === 0 ? (
						<EmptyHint icon={CalendarClock} text="No follow-ups scheduled. Set one from any contact's record." />
					) : (
						<ul className="divide-y divide-subtle">
							{followUps.map((c) => {
								const due = c.follow_up_date ? new Date(String(c.follow_up_date)) : null;
								const overdue = due && due.getTime() < Date.now();
								return (
									<li key={String(c.id)} className="flex items-center justify-between gap-3 py-2">
										<div className="min-w-0">
											<Link to={`/contacts`} className="block truncate text-sm font-medium text-primary">{fullName(c)}</Link>
											<p className="truncate text-xs text-muted">
												{String((c.company as ApiRecord | null)?.name ?? '')}{c.follow_up_note ? ` — ${String(c.follow_up_note)}` : ''}
											</p>
										</div>
										<span className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-medium ${overdue ? 'bg-danger/15 text-danger' : 'bg-surface-hover text-secondary'}`}>
											{formatRelativeDate(c.follow_up_date)}
										</span>
									</li>
								);
							})}
						</ul>
					)}
				</SurfaceCard>

				<SurfaceCard
					title="My open deals"
					action={<Link to="/pipelines" className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-xs text-secondary hover:bg-surface-hover">Open pipelines <ArrowRight className="h-3.5 w-3.5" /></Link>}
				>
					{openOpps.length === 0 ? (
						<EmptyHint icon={KanbanSquare} text="No open deals owned by you. Pick a pipeline to take work." />
					) : (
						<ul className="divide-y divide-subtle">
							{openOpps.map((o) => (
								<li key={String(o.id)} className="flex items-center justify-between gap-3 py-2">
									<div className="min-w-0">
										<p className="truncate text-sm font-medium text-primary">{String(o.title ?? `#${o.id}`)}</p>
										<p className="truncate text-xs text-muted">
											{String((o.company as ApiRecord | null)?.name ?? 'No company')}
											{(o.vendor as ApiRecord | null)?.name ? ` · ${String((o.vendor as ApiRecord).name)}` : ''}
										</p>
									</div>
									<span className="shrink-0 rounded-full bg-surface-hover px-2 py-0.5 text-[10px] font-medium text-secondary">{String(o.stage ?? 'New')}</span>
								</li>
							))}
						</ul>
					)}
				</SurfaceCard>
			</section>

			<section className="grid gap-3 xl:grid-cols-[1fr_1fr]">
				<SurfaceCard
					title="New leads waiting"
					action={<Link to="/leads" className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-xs text-secondary hover:bg-surface-hover">Open leads <ArrowRight className="h-3.5 w-3.5" /></Link>}
				>
					{newLeads.length === 0 ? (
						<EmptyHint icon={Sparkles} text="The lead queue is empty. Send some intake from your research tools." />
					) : (
						<ul className="space-y-1.5">
							{newLeads.map((c) => (
								<li key={String(c.id)} className="rounded-xl border border-subtle bg-surface-2 px-3 py-2">
									<p className="text-sm font-medium text-primary">{fullName(c)}</p>
									<p className="text-xs text-muted">
										{String((c.company as ApiRecord | null)?.name ?? '')}
										{c.title ? ` · ${String(c.title)}` : ''}
									</p>
								</li>
							))}
						</ul>
					)}
				</SurfaceCard>

				<SurfaceCard
					title="Your business units"
					action={<Link to="/pipelines" className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-surface-2 px-3 py-1.5 text-xs text-secondary hover:bg-surface-hover">All pipelines <ArrowRight className="h-3.5 w-3.5" /></Link>}
				>
					{businessUnits.length === 0 ? (
						<EmptyHint icon={Building2} text="No business units configured yet. Add one in Admin → Vendors." />
					) : (
						<div className="grid gap-2 sm:grid-cols-2">
							{businessUnits.map((bu) => (
								<Link
									key={String(bu.id)}
									to={`/pipelines?bu=${bu.id}`}
									className="rounded-xl border border-subtle bg-surface-2 px-3 py-2 transition hover:bg-surface-hover"
								>
									<p className="text-sm font-medium text-primary">{String(bu.name)}</p>
									<p className="mt-1 text-[11px] text-muted">
										<span className="text-accent">{String(bu.open_count ?? 0)}</span> open · {String(bu.won_count ?? 0)} won
										{bu.uses_defaults ? ' · default stages' : ''}
									</p>
								</Link>
							))}
						</div>
					)}
				</SurfaceCard>
			</section>

			<section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
				<QuickLink to="/dialer" label="Power dialer" icon={PhoneCall} hint="Call lists & click-to-dial" />
				<QuickLink to="/inbox" label="Inbox" icon={Mail} hint="Inbound across all channels" />
				<QuickLink to="/companies" label="Companies" icon={Building2} hint="Browse the book" />
				<QuickLink to="/campaigns" label="Campaigns" icon={Users} hint="Email outreach" />
			</section>
		</div>
	);
}

function getGreeting() {
	const h = new Date().getHours();
	if (h < 5) return 'evening';
	if (h < 12) return 'morning';
	if (h < 17) return 'afternoon';
	return 'evening';
}

function getSubtitle(followUps: number, overdue: number, deals: number, leads: number) {
	if (overdue > 0) return `${overdue} follow-up${overdue === 1 ? '' : 's'} overdue — start there.`;
	if (followUps > 0) return `${followUps} follow-up${followUps === 1 ? '' : 's'} scheduled for today.`;
	if (leads > 0) return `${leads} new lead${leads === 1 ? '' : 's'} ready to work.`;
	if (deals > 0) return `${deals} open deal${deals === 1 ? '' : 's'} in your pipeline.`;
	return 'Inbox is clear. Time to prospect.';
}

function MiniStat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: 'urgent' | 'normal' }) {
	return (
		<div className={`rounded-lg border px-3 py-2.5 ${accent === 'urgent' ? 'border-danger/30 bg-danger/10' : 'border-subtle bg-surface'}`}>
			<p className="text-[10px] font-medium uppercase tracking-[0.25em] text-muted">{label}</p>
			<p className="mt-0.5 text-xl font-semibold text-primary">{value}</p>
			{sub ? <p className="text-[11px] text-muted">{sub}</p> : null}
		</div>
	);
}

function EmptyHint({ icon: Icon, text }: { icon: typeof CalendarClock; text: string }) {
	return (
		<div className="flex items-center gap-3 rounded-xl border border-dashed border-subtle px-3 py-4 text-sm text-muted">
			<Icon className="h-4 w-4 shrink-0 text-muted" />
			<span>{text}</span>
		</div>
	);
}

function QuickLink({ to, label, icon: Icon, hint }: { to: string; label: string; icon: typeof PhoneCall; hint: string }) {
	return (
		<Link to={to} className="flex items-start gap-3 rounded-2xl border border-subtle bg-surface-2/80 p-3 shadow-panel transition hover:bg-surface-2">
			<div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent text-accent-contrast">
				<Icon className="h-4 w-4" />
			</div>
			<div>
				<p className="text-sm font-semibold text-primary">{label}</p>
				<p className="text-[11px] text-muted">{hint}</p>
			</div>
		</Link>
	);
}
